/*---------------------------------------------------------------------------------------------
 *  Copyright (c) by Solusek Systems. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import { Modal } from "react-bootstrap";
import { qreq } from "../../shared/qrequest";
import AppActions from "../AppActions";
import L from "../Lang"
import BaseCom from "../BaseCom";
import Card from "../Card";
import FormInput from "../FormInput";
import Icon from "../Icon";
import { withParamsAndNavigate } from "../WithParamsAndNavigate";
import PanelTargetManageEdit from "./PanelTargetManageEdit";

class PanelManageEdit extends BaseCom {
    constructor(props) {
        super(props);
        this.state = {
            item: {
                targets: []
            },
            example: {
                name: 'Complete Blood Count with Differential',
                code: 'CBCD',
                shortName: 'CBC Diff'
            }
        }
    }

    componentDidMount() {
        this.fillList('/api/productline/list', 'productLines');
        this.fillList('/api/specimentype/list', 'specimenTypes');
        this.loadTargets();

        if (this.props.params?.id && this.props.params.id !== 'new') {
            this.fillItem('/api/panel/get/' + this.props.params.id, 'item');
        }
    }

    change = () => {
        this.setState({ item: { ...this.state.item } });
    }

    loadTargets = async () => {
        await this.fillListAsync('/api/target/list', 'targets');
    }

    toggleTarget = (target) => {
        if (!target) return;
        this.setState(state => {
            const targets = state.item.targets ?? [];
            const selected = targets.some(t => String(t.id) === String(target.id));
            return {
                item: {
                    ...state.item, targets: selected
                        ? targets.filter(t => String(t.id) !== String(target.id)) : [...targets, target]
                }
            };
        });
    }

    getSelectedTarget = (id) => {
        return (this.state.item.targets ?? []).some(t => String(t.id) === String(id));
    }

    filterTargets = () => {
        const query = (this.state.targetSearch ?? '').trim().toLowerCase();
        return (this.state.targets ?? []).filter(target =>
            (!this.state.selectedOnly || this.getSelectedTarget(target.id)) &&
            (!query || [target.name, target.code].some(value => String(value ?? '').toLowerCase().includes(query))));
    }

    // Suggest AI

    suggest = async () => {
        if (this.state.suggesting || this.state.saving) return;
        const productLine = (this.state.productLines ?? []).find(i => String(i.id) === String(this.state.item.productLineID));
        if (!productLine || !this.state.item.name?.trim()) {
            this.alert('Enter a panel name and select a product line first.');
            return;
        }
        this.setState({ suggesting: true });
        try {
            const j = await qreq.getAsync(`/api/panel/suggest-targets?productLineName=${encodeURIComponent(productLine.name)}&panelName=${encodeURIComponent(this.state.item.name)}`);
            if (Array.isArray(j.list)) this.setState({ suggestedTargets: j.list.map(i => ({ ...i, selected: false })), showSuggest: true });
            else this.alert(j.errorMessage || 'Unable to suggest targets.');
        }
        catch { this.unkownErrorCallback(); }
        finally { this.setState({ suggesting: false }); }
    }

    selectAllSuggested = (e) => {
        const selected = e.target.checked;
        this.setState(state => ({ suggestedTargets: (state.suggestedTargets ?? []).map(i => ({ ...i, selected })) }));
    }

    commitSuggested = async (e) => {
        e?.preventDefault();
        if (this.state.suggestProcessing) return;
        const list = (this.state.suggestedTargets ?? []).filter(i => i.selected);
        if (!list.length) return;
        this.setState({ suggestProcessing: true });
        const created = [];
        const failed = [];
        try {
            for (const target of list) {
                try {
                    const j = await qreq.postAsync('/api/target/save', target);
                    if ((j.errorCode === 0 || j.errorCode === 7) && j.item?.id != null) created.push(j.item);
                    else failed.push(target);
                }
                catch { failed.push(target); }
            }
            this.setState(state => {
                const targets = [...(state.item.targets ?? [])];
                for (const target of created) {
                    if (!targets.some(i => String(i.id) === String(target.id))) targets.push(target);
                }
                return { item: { ...state.item, targets }, suggestedTargets: failed, showSuggest: failed.length > 0 };
            });
            if (failed.length) this.alert('Some targets could not be added. They remain selected for retry.');
            await this.loadTargets();
        }
        catch { this.unkownErrorCallback(); }
        finally { this.setState({ suggestProcessing: false }); }
    }

    toggleSuggestedTarget(e, target) {
        const selected = e.target.checked;
        this.setState(state => ({ suggestedTargets: state.suggestedTargets.map(i => i === target ? { ...i, selected } : i) }));
    }

    submit = async (e) => {
        e?.preventDefault();
        if (this.state.saving || this.state.suggesting) return;
        this.setState({ saving: true });
        try {
            let j = await qreq.postAsync('/api/panel/save', this.state.item);
            if (j.errorCode === 0) {
                this.success('Panel saved');
                this.goBack();
            }
            else
                this.alert(j.errorMessage || 'Error saving panel');
        }
        catch (e) {
            this.alert(e);
        }
        finally {
            this.setState({ saving: false });
        }
    }

    submitTarget = (e) => {
        this.setState({ showTargetModal: false });
        this.loadTargets();
    }

    render() {
        return <div className="container-fluid u-page">
            {/* SUGGEST */}
            <Modal show={this.state.showSuggest} onHide={() => { if (!this.state.suggestProcessing) this.setState({ showSuggest: false }); }}>
                <Modal.Header closeButton={!this.state.suggestProcessing}><Modal.Title><L>Suggested Targets</L></Modal.Title></Modal.Header>
                <form onSubmit={this.commitSuggested}>
                    <Modal.Body>
                        {this.state.suggestedTargets && <fieldset disabled={this.state.suggestProcessing} className="border-0 p-0 m-0"><p className="text-body-secondary small"><L>Review suggested targets and reference ranges before applying them.</L></p><div className="overflow-auto" style={{ maxHeight: '24rem' }}>
                            <ul className="list-group no-select">
                                <li className="list-group-item d-flex">
                                    <input type="checkbox" className="d-block" aria-label="Select all suggested targets" checked={!!this.state.suggestedTargets?.length && this.state.suggestedTargets.every(i => i.selected)} onChange={this.selectAllSuggested} />
                                    <div className="ms-2 flex-fill"><L>Select All</L></div>
                                </li>
                                {this.state.suggestedTargets.map((i, idx) => <li key={idx} className={'list-group-item d-flex'}>
                                    <input type="checkbox" className="d-block" aria-label={`Select ${i.name}`} checked={!!i.selected} onChange={(e) => this.toggleSuggestedTarget(e, i)} />
                                    <div className="ms-2 flex-fill">
                                        {i.name}
                                    </div>
                                    <div className="ms-2">
                                        {i.code}
                                    </div>
                                </li>)}
                            </ul>
                        </div></fieldset>}
                    </Modal.Body>
                    <Modal.Footer>
                        <button type="submit" className="btn btn-success" disabled={this.state.suggestProcessing || !this.state.suggestedTargets?.some(i => i.selected)}>{this.state.suggestProcessing && <Icon icon="faWandMagicSparkles" spin />} Apply</button>
                        <button type="button" className="btn btn-secondary" onClick={() => this.setState({ showSuggest: false })} disabled={this.state.suggestProcessing}>Close</button>
                    </Modal.Footer>
                </form>
            </Modal>

            {/* TARGET */}
            {this.state.showTargetModal && <Modal show={this.state.showTargetModal} onHide={() => this.setState({ showTargetModal: false })} size="xl">
                <Modal.Header>
                    <Modal.Title>
                        <L>Create Target</L>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <PanelTargetManageEdit onSubmit={this.submitTarget} formId="panel-new-target-form" modal />
                </Modal.Body>
                <Modal.Footer>
                    <button type="submit" form="panel-new-target-form" className="btn btn-success btn-lg"><Icon icon="faSave" /> <L>Save</L></button>
                    <button type="button" className="btn btn-secondary btn-lg" onClick={() => this.setState({ showTargetModal: false })}><Icon icon="faClose" /> <L>Close</L></button>
                </Modal.Footer>
            </Modal>}

            <form onSubmit={this.submit}>
                <header className="u-header">
                    <h1><L>{this.props.params?.id && this.props.params.id !== 'new' ? 'Edit Panel' : 'New Panel'}</L></h1>
                    <p className="u-intro"><L>Configure panel details, select targets, and control ordering.</L></p>
                </header>
                <Card title="Basic Information">
                    <div className="row g-3">
                        <div className="col-md-5">
                            <FormInput model={this.state.item} name="name" label="Name" required placeholder={this.state.example.name} onChange={this.change} />
                        </div>
                        <div className="col-md-2">
                            <FormInput model={this.state.item} name="code" label="Code" required placeholder={this.state.example.code} onChange={this.change} />
                        </div>
                        <div className="col-md-5">
                            <FormInput model={this.state.item} name="shortName" label="Short Name" placeholder={this.state.example.shortName} onChange={this.change} />
                        </div>
                    </div>
                    <div className="row g-3">
                        <div className="col-md-6">
                            <FormInput model={this.state.item} name="specimenTypeID" label="Specimen Type" type="select" options={this.state.specimenTypes ?? []} onChange={this.change} required />
                        </div>
                        <div className="col-md-6">
                            <FormInput model={this.state.item} name="productLineID" label="Product Line" type="select" options={this.state.productLines ?? []} onChange={this.change} required />
                        </div>
                    </div>
                    {this.state.item.name && this.state.item.productLineID && <>
                        <button type="button" className="btn btn-outline-primary" disabled={this.state.suggesting || this.state.saving} onClick={this.suggest}><Icon icon="faWandMagicSparkles" spin={this.state.suggesting} /><L>Suggest Targets</L></button>
                    </>}
                </Card>
                <Card title="Targets" headerButtons={<>
                    <button type="button" className="btn btn-primary" onClick={() => this.setState({ showTargetModal: true })}>
                        <Icon icon="faPlus" /> <L>New Target</L>
                    </button>
                </>}>
                    <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
                        <input type="search" className="form-control flex-grow-1" style={{ flexBasis: '240px' }} aria-label="Search targets" placeholder="Search targets by name or code..." value={this.state.targetSearch ?? ''} onChange={e => this.setState({ targetSearch: e.target.value })} />
                        <label className="d-flex align-items-center gap-2"><input type="checkbox" className="form-check-input m-0" checked={!!this.state.selectedOnly} onChange={e => this.setState({ selectedOnly: e.target.checked })} /><L>Selected only</L></label>
                        <span className="badge bg-primary" role="status">{(this.state.item.targets ?? []).length} <L>selected</L></span>
                    </div>
                    {!this.filterTargets().length && <p className="text-body-secondary" role="status"><L>No matching targets.</L></p>}
                    <div className="overflow-auto" style={{ maxHeight: '24rem' }}>
                        <ul className="list-group no-select">
                            {this.filterTargets()?.map(i => <li key={i.id} className={'list-group-item d-flex' + (this.getSelectedTarget(i.id) ? ' active' : '')}>
                                <input type="checkbox" className="form-check-input flex-shrink-0" aria-label={`Select ${i.name}`} checked={this.getSelectedTarget(i.id)} onChange={() => this.toggleTarget(i)} />
                                <div className="ms-2 flex-fill">
                                    {i.name}
                                </div>
                                <div className="ms-2">
                                    {i.code}
                                </div>
                            </li>)}
                        </ul>
                    </div>
                    {this.state.item?.targets?.length > 0 && <div className="mt-2">
                        <div className="mb-1">Selected Targets:</div>
                        {this.state.item?.targets?.map(i => <span key={i.id} className="badge bg-primary me-1 mb-1">{i.name} ({i.code}) <button type="button" className="btn-close btn-close-white ms-2" aria-label={`Remove ${i.name}`} onClick={() => this.toggleTarget(i)}></button></span>)}
                    </div>}
                </Card>
                <Card title="Settings">
                    <div className="row g-3">
                        <div className="col-md-4">
                            <FormInput model={this.state.item} name="isOrderable" label="Orderable" type="switch" onChange={this.change} />
                            <small className="text-body-secondary"><L>Allow this panel to be ordered.</L></small>
                        </div>
                        <div className="col-md-4">
                            <FormInput model={this.state.item} name="isPatientOrderable" label="Patient Orderable" type="switch" onChange={this.change} />
                            <small className="text-body-secondary"><L>Allow patients to order this panel.</L></small>
                        </div>
                        <div className="col-md-4">
                            <FormInput model={this.state.item} name="isDisabled" label="Disable" type="switch" onChange={this.change} />
                            <small className="text-body-secondary"><L>Disable the panel completely from use.</L></small>
                        </div>
                    </div>
                </Card>
                <AppActions>
                    <button type="submit" className="btn btn-success btn-lg" disabled={this.state.saving || this.state.suggesting}><Icon icon="faSave" /> <L>{this.state.saving ? 'Saving...' : 'Save'}</L></button>
                </AppActions>
            </form>
        </div>;
    }
}

export default withParamsAndNavigate(PanelManageEdit);